import React, { useEffect, useMemo, useState } from 'react';

import { NavigationType, useLocation, useNavigationType, useNavigate, useParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';

import { Permissions } from 'src/app_deprecated/constants/PermissionsConstants';

import { CaretDown } from 'src/app/components/icons';
import { Button } from 'src/app/components/lib/button';
import { ControlBar, ControlGroup, ControlSearchBox } from 'src/app/components/lib/control-bar';
import { Loader } from 'src/app/components/lib/loader';
import { MenuItem } from 'src/app/components/lib/menu-item';
import {
  StatusContainer,
  PaginationRange,
  PageUnits,
  FooterSelect,
} from 'src/app/components/lib/table/footer/footer-rebrand.styles';
import { RebrandPagination } from 'src/app/components/lib/table/footer/rebrand-pagination';
import { NoResultsOverlay } from 'src/app/components/lib/table/no-results-overlay';
import { useDeleteCampaignTemplateMutation } from 'src/app/queries/marketing/campaign-templates/delete-campaign-template';
import { useGetTemplatesQuery } from 'src/app/queries/marketing/campaign-templates/get-campaign-templates';
import { Modal, useModal } from 'src/app/state/modals';
import { useNotificationActions } from 'src/app/state/notifications';
import { usePermissionCheck } from 'src/app/state/user-permissions';

import { FormActionEllipsis } from '../../../campaigns/marketing-campaign-builder/shared-slides/campaign-setup-and-editor-builder/form-action-ellipsis';
import { doesTemplateHaveContent, useDebounce } from '../helpers';
import { initialWizardStateAtom } from '../slides/atoms/initial-wizard-state-atom';
import { TemplatePreviewModal } from '../slides/template-select/template-preview-modal';
import { CampaignTemplateBuilder } from '../wizard';

import {
  GridContainer,
  TemplateCardWrapper,
  TemplatePreview,
  SkeletonTemplatePreview,
  HtmlPreviewIframe,
  TemplateFooter,
  TemplateName,
  MoreButton,
  PageContent,
  StyledContentCard,
  SkeletonCard,
  NoResultsWrapper,
  StyledPaginationControlContainer,
  RightSideControls,
} from './template-list.styles';

import type { MarketingTemplatePreset } from 'src/app/queries/marketing/campaign-templates/get-campaign-templates';

function TemplateList() {
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  const debouncedSearchFilter = useDebounce(searchFilter, 1000);
  const [_, setInitialWizardState] = useRecoilState(initialWizardStateAtom);

  // params
  const params = useParams()?.['*'];
  const drawerOpen = useMemo(() => {
    const isEditOrCreate = params?.includes('edit') || params?.includes('create');
    return !!isEditOrCreate;
  }, [params]);
  const navigate = useNavigate();
  const notifications = useNotificationActions();

  const canViewCreateOrEditTemplates = usePermissionCheck(
    [Permissions.ViewEmailTemplates, Permissions.CreateEditEmailTemplates],
    'or'
  );

  // Reset wizard state when user navigates back
  const navigationType = useNavigationType();
  const location = useLocation();

  useEffect(() => {
    if (navigationType === NavigationType.Pop) {
      setInitialWizardState({
        isNewTemplate: false,
        isSet: false,
      });
    }
  }, [navigationType, setInitialWizardState, location.key]);

  const { mutateAsync: deleteCampaignTemplate, isLoading: _isDeleting } = useDeleteCampaignTemplateMutation();

  const handleDrawerClose = () => {
    navigate('/marketing/configure/templates');

    return null;
  };

  const { data: templates = { Data: [], Page: 0, TotalPages: 0, TotalRows: 0 }, isLoading } = useGetTemplatesQuery({
    CurrentPage: currentPage,
    PageSize: pageSize,
    Sorting: [],
    Filters:
      debouncedSearchFilter.length > 0
        ? [
            {
              Key: 'TemplateName',
              Operator: 'Like',
              Value: debouncedSearchFilter,
            },
          ]
        : [],
  });

  const rows = useMemo(() => templates.Data ?? [], [templates]);

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedListItem, setSelectedListItem] = useState<number | 'new' | null>(null);
  const confirmModal = useModal(Modal.confirm);

  const selectedTemplateHTML = useMemo(
    () => rows.find((template) => template.MarketingTemplatePresetId === selectedListItem)?.HtmlBody,
    [selectedListItem, rows]
  );

  const handleSearchFilterChange = (value: string) => {
    setSearchFilter(value);
    setCurrentPage(0); // Reset to first page when search changes
  };

  const handleTemplateClick = (template: MarketingTemplatePreset) => {
    navigate(`/marketing/configure/templates/edit/${template.MarketingTemplatePresetId}`);
  };

  const handleCardClick = (template: MarketingTemplatePreset) => {
    // Don't allow editing of templates without HTML/JSON content
    if (!doesTemplateHaveContent(template)) {
      return;
    }
    handleTemplateClick(template);
  };

  const handleCreateTemplate = () => {
    navigate('/marketing/configure/templates/create');
  };

  const handleDeleteTemplate = async (template: MarketingTemplatePreset) => {
    try {
      await deleteCampaignTemplate(template.MarketingTemplatePresetId);
      notifications.success({
        message: 'Template deleted',
      });
    } catch (error) {
      notifications.error({
        message: 'Failed to delete template',
      });
    }
  };

  const handlePreviewTemplate = (template: MarketingTemplatePreset) => {
    // Don't allow preview of templates without HTML/JSON content
    if (!doesTemplateHaveContent(template)) {
      return;
    }
    setSelectedListItem(template.MarketingTemplatePresetId);
    setShowPreviewModal(true);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(0); // Reset to first page when page size changes
  };

  // Create skeleton cards for loading state
  const renderSkeletonCards = () =>
    Array.from({ length: 4 }, (_, index) => (
      <SkeletonCard key={`skeleton-${index}`}>
        <StyledContentCard addContentPadding={false}>
          <SkeletonTemplatePreview>
            <Loader size='3x' variant='grey' />
          </SkeletonTemplatePreview>
          <TemplateFooter>
            <TemplateName>Loading template...</TemplateName>
            <MoreButton aria-label='More options' disabled>
              ⋯
            </MoreButton>
          </TemplateFooter>
        </StyledContentCard>
      </SkeletonCard>
    ));

  return (
    <>
      <ControlBar>
        <ControlGroup grow>
          <ControlSearchBox
            initialValue={searchFilter}
            placeholder='Search templates...'
            onChange={handleSearchFilterChange}
          />
          {canViewCreateOrEditTemplates && (
            <Button label='Create new template' variant='primary' onClick={handleCreateTemplate} />
          )}
        </ControlGroup>
      </ControlBar>

      {isLoading ? (
        <PageContent>
          <GridContainer>{renderSkeletonCards()}</GridContainer>
        </PageContent>
      ) : (
        <PageContent>
          <GridContainer>
            {rows.map((template) => {
              const hasTemplateContent = Boolean(doesTemplateHaveContent(template));
              return (
                <TemplateCardWrapper key={template.MarketingTemplatePresetId}>
                  <StyledContentCard addContentPadding={false}>
                    <TemplatePreview
                      data-testid='template-preview'
                      disabled={!hasTemplateContent}
                      onClick={() => handleCardClick(template)}
                    >
                      <HtmlPreviewIframe
                        sandbox='allow-same-origin'
                        srcDoc={
                          template.HtmlBody ||
                          '<h1 style="font-size: 48px; text-align: center; padding: 20px; color: #999;">No preview available</h1>'
                        }
                      />
                    </TemplatePreview>
                    <TemplateFooter>
                      <TemplateName>{template.TemplateName}</TemplateName>
                      <FormActionEllipsis
                        actions={[
                          {
                            label: 'Preview',
                            onClick: () => handlePreviewTemplate(template),
                            visible: hasTemplateContent,
                          },
                          {
                            label: 'Remove',
                            onClick: () => {
                              confirmModal.openModal({
                                title: 'Remove template',
                                helpText: `Are you sure you want to remove "${template.TemplateName}"? You will not be able to undo this action.`,
                                onConfirm: () => {
                                  void handleDeleteTemplate(template);
                                },
                              });
                            },
                            visible: true,
                          },
                        ]}
                        useRectangularStyle
                      />
                    </TemplateFooter>
                  </StyledContentCard>
                </TemplateCardWrapper>
              );
            })}
          </GridContainer>
          {rows.length === 0 && !isLoading && (
            <NoResultsWrapper>
              <NoResultsOverlay
                noResultsHeading='No data available'
                noResultsSubheading="We were unable to find data that matches what you're looking for. Please try again."
              />
            </NoResultsWrapper>
          )}
          <TemplatePreviewModal
            isOpen={showPreviewModal}
            templateHTML={selectedTemplateHTML ?? ''}
            onClose={() => setShowPreviewModal(false)}
          />
        </PageContent>
      )}
      {rows.length > 0 && !isLoading && (
        <StyledPaginationControlContainer>
          <RebrandPagination
            count={templates.TotalPages}
            page={currentPage + 1}
            onChange={(event, value) => handlePageChange(value - 1)}
          />
          <RightSideControls>
            <StatusContainer>
              Displaying{' '}
              <PaginationRange>
                {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, templates.TotalRows)}
              </PaginationRange>{' '}
              of <span>{templates.TotalRows}</span>
            </StatusContainer>
            <FooterSelect
              IconComponent={CaretDown}
              value={pageSize.toString()}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            >
              {[5, 10, 25].map((size) => (
                <MenuItem key={String(size)} value={String(size)}>
                  {size}
                </MenuItem>
              ))}
            </FooterSelect>
            <PageUnits>Per page</PageUnits>
          </RightSideControls>
        </StyledPaginationControlContainer>
      )}
      {canViewCreateOrEditTemplates && drawerOpen && <CampaignTemplateBuilder isOpen onClose={handleDrawerClose} />}
    </>
  );
}

export default TemplateList;
